import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { RootLayout } from './components/layout/RootLayout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { AuthCallback } from './components/auth/AuthCallback';
import { UserRole } from './constants/roles';
import { Loader2 } from 'lucide-react';

// Lazy load components for code splitting
const AdminLayout = lazy(() => import('./components/layout/AdminLayout').then(m => ({ default: m.AdminLayout })));
const ApproverLayout = lazy(() => import('./components/layout/ApproverLayout').then(m => ({ default: m.ApproverLayout })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const UserDetails = lazy(() => import('./pages/UserDetails').then(m => ({ default: m.UserDetails })));
const ApproverDashboard = lazy(() => import('./pages/ApproverDashboard').then(m => ({ default: m.ApproverDashboard })));
const RequestDetails = lazy(() => import('./pages/RequestDetails').then(m => ({ default: m.RequestDetails })));
const ToolsPage = lazy(() => import('./pages/admin/ToolsPage').then(m => ({ default: m.ToolsPage })));
const UsersPage = lazy(() => import('./pages/admin/UsersPage').then(m => ({ default: m.UsersPage })));
const RequestsPage = lazy(() => import('./pages/admin/RequestsPage').then(m => ({ default: m.RequestsPage })));
const CostsPage = lazy(() => import('./pages/admin/CostsPage').then(m => ({ default: m.CostsPage })));
const ToolDetails = lazy(() => import('./pages/admin/ToolDetails').then(m => ({ default: m.ToolDetails })));
const ClientsPage = lazy(() => import('./pages/admin/ClientsPage').then(m => ({ default: m.ClientsPage })));
const ClientDetails = lazy(() => import('./pages/admin/ClientDetails').then(m => ({ default: m.ClientDetails })));

// Loading fallback
const LoadingSpinner = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

function App() {
  return (
    <Routes>
      <Route element={<RootLayout />}>
        {/* Public routes */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* User dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={[UserRole.USER, UserRole.ADMIN]}>
              <Suspense fallback={<LoadingSpinner />}>
                <Dashboard />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
              <Suspense fallback={<LoadingSpinner />}>
                <AdminLayout />
              </Suspense>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="tools" replace />} />
          <Route
            path="tools"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <ToolsPage />
              </Suspense>
            }
          />
          <Route
            path="tools/:toolId"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <ToolDetails />
              </Suspense>
            }
          />
          <Route
            path="users"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <UsersPage />
              </Suspense>
            }
          />
          <Route
            path="users/:userId"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <UserDetails />
              </Suspense>
            }
          />
          <Route
            path="requests"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <RequestsPage />
              </Suspense>
            }
          />
          <Route
            path="costs"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <CostsPage />
              </Suspense>
            }
          />
          <Route
            path="clients"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <ClientsPage />
              </Suspense>
            }
          />
          <Route
            path="clients/:clientId"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <ClientDetails />
              </Suspense>
            }
          />
        </Route>

        {/* Approver routes */}
        <Route
          path="/approver"
          element={
            <ProtectedRoute allowedRoles={[UserRole.USER, UserRole.ADMIN]} requireApprover={true}>
              <Suspense fallback={<LoadingSpinner />}>
                <ApproverLayout />
              </Suspense>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="requests" replace />} />
          <Route
            path="requests"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <ApproverDashboard />
              </Suspense>
            }
          />
          <Route
            path="requests/:requestId"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <RequestDetails />
              </Suspense>
            }
          />
        </Route>

        {/* User request details */}
        <Route
          path="/requests/:requestId"
          element={
            <ProtectedRoute allowedRoles={[UserRole.USER, UserRole.ADMIN]}>
              <Suspense fallback={<LoadingSpinner />}>
                <RequestDetails />
              </Suspense>
            </ProtectedRoute>
          }
        />
      </Route>
    </Routes>
  );
}

export default App;
